import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { ConfidenceBadge, deriveFieldStatus } from './ConfidenceBadge';
import { Pencil, Check, Eye } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';


// ── Source Evidence (hidden by default) ──
const SourceEvidence = ({ sourceRef, sourceSnippet }: { sourceRef?: SourceRef; sourceSnippet?: string | null }) => {
  const [visible, setVisible] = useState(false);
  if (!sourceRef && !sourceSnippet) return null;

  return (
    <div className="mt-2">
      {!visible ? (
        <button
          onClick={() => setVisible(true)}
          className="text-[10px] text-muted-foreground/35 hover:text-muted-foreground/55 transition-colors flex items-center gap-1"
        >
          <Eye className="h-2.5 w-2.5" />
          Begründung anzeigen
        </button>
      ) : (
        <div className="flex items-center gap-2 animate-fade-in">
          {sourceRef && (
            <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/50 bg-muted/30 px-1.5 py-0.5 rounded">
              📄 {sourceRef.file_name}{sourceRef.page_number ? `, S. ${sourceRef.page_number}` : ''}
            </span>
          )}
          {sourceSnippet && (
            <p className="text-[11px] text-muted-foreground/40 italic truncate max-w-[300px]">
              „{sourceSnippet}"
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export type DetectionStatus = 'detected' | 'missing' | 'conflict';
export type ConfirmationStatus = 'unreviewed' | 'accepted' | 'edited' | 'confirmed';

export interface SourceRef {
  document_id: string;
  chunk_id: string;
  file_name: string;
  page_number?: number;
}

export interface ExtractionFieldData {
  value: unknown;
  confidence: number;
  source: 'llm' | 'user' | 'document';
  detection_status: DetectionStatus;
  confirmation_status: ConfirmationStatus;
  source_snippet: string | null;
  source_ref?: SourceRef;
  updated_at?: string;
}

interface ExtractionFieldProps {
  label: string;
  field: ExtractionFieldData;
  onUpdate: (newValue: string) => void;
  multiline?: boolean;
  className?: string;
  prominent?: boolean;
}

export const ExtractionField = ({ label, field, onUpdate, multiline, className, prominent }: ExtractionFieldProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(field.value ?? ''));
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const fieldStatus = deriveFieldStatus(field);

  useEffect(() => {
    setEditValue(String(field.value ?? ''));
  }, [field.value]);

  useEffect(() => {
    if (isEditing && inputRef.current) inputRef.current.focus();
  }, [isEditing]);

  const handleConfirm = () => {
    setIsEditing(false);
    onUpdate(editValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleConfirm(); }
    if (e.key === 'Escape') { setEditValue(String(field.value ?? '')); setIsEditing(false); }
  };

  return (
    <div className={cn(
      'group relative rounded-lg border bg-card p-4 transition-all duration-200',
      prominent ? 'border-primary/20 bg-primary/[0.015]' : 'border-border/50',
      fieldStatus === 'missing' && 'border-destructive/20 bg-destructive/[0.015]',
      fieldStatus === 'low_confidence' && 'border-amber-500/20 bg-amber-500/[0.015]',
      fieldStatus === 'confirmed' && 'border-emerald-500/15 bg-emerald-500/[0.01]',
      className,
    )}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-[11px] font-medium text-muted-foreground/60 uppercase tracking-[0.12em]">{label}</span>
        <div className="flex items-center gap-1.5">
          <ConfidenceBadge field={field} />
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-muted"
              aria-label="Bearbeiten"
            >
              <Pencil className="h-3 w-3 text-muted-foreground/60" />
            </button>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="flex gap-2 items-end">
          {multiline ? (
            <Textarea
              ref={inputRef as React.Ref<HTMLTextAreaElement>}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="min-h-[60px] text-sm border-border/60"
            />
          ) : (
            <input
              ref={inputRef as React.Ref<HTMLInputElement>}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-background border border-border/60 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
          )}
          <button
            onClick={handleConfirm}
            className="shrink-0 p-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            aria-label="Bestätigen"
          >
            <Check className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <p className={cn(
          'text-sm leading-relaxed',
          prominent ? 'font-medium text-foreground' : 'text-foreground/85',
          fieldStatus === 'missing' && 'italic text-muted-foreground/50',
        )}>
          {String(field.value ?? '') || '—'}
        </p>
      )}

      {(field.source_snippet || field.source_ref) && !isEditing && (
        <SourceEvidence sourceRef={field.source_ref} sourceSnippet={field.source_snippet} />
      )}
    </div>
  );
};
